import { Button, Flex, Form, Input, Modal, Select } from 'antd'
import React, { useState } from 'react'

import { MfaSchoolRequirementService } from '@services/MfaSchoolRequirementService'
import { useConfirmation } from '@store/confirmation'
import { useNotifications } from '@store/notifications'

import type { MfaSchoolRequirement, School } from '../../../../types'

type ModalProps = {
    school: School
    isOpen: boolean
    onClose: () => void
    refetch: () => Promise<void>
}

type FieldType = {
    mfaLevel: 'ALL' | 'ADMIN_ONLY'
    adminLeeway: string
    userLeeway: string
}

export const MfaDetailsModal: React.FC<ModalProps> = ({ school, isOpen, onClose, refetch }) => {
    const [isLoading, setIsLoading] = useState(false)
    const { showNotification } = useNotifications()
    const confirm = useConfirmation()

    const { id: schoolId, mfaSchoolRequirement } = school

    const confirmDescription = mfaSchoolRequirement
        ? 'MFA requirements will be updated for this school. Existing users will not be affected.'
        : 'MFA requirements will be added to this school.'

    const handleSubmit = async (values: FieldType) => {
        if (
            await confirm({
                title: `${mfaSchoolRequirement ? 'Edit' : 'Create'} MFA Configuration`,
                description: confirmDescription
            })
        ) {
            const settings = {
                adminLeeway: values.adminLeeway,
                userLeeway: values.userLeeway
            }
            const formattedData: Partial<MfaSchoolRequirement> = {
                schoolId: schoolId,
                mfaLevel: values.mfaLevel,
                settings
            }
            try {
                setIsLoading(true)

                if (mfaSchoolRequirement) {
                    await MfaSchoolRequirementService.update(mfaSchoolRequirement.id, formattedData)
                } else {
                    await MfaSchoolRequirementService.create(formattedData)
                }

                await refetch()

                onClose()
            } catch (error) {
                showNotification({
                    title: 'Error updating subscription',
                    type: 'error'
                })
            } finally {
                setIsLoading(false)
            }
        }
    }

    return (
        <Modal open={isOpen} onCancel={onClose} footer={null}>
            <Form labelCol={{ span: 6 }} wrapperCol={{ span: 14 }} layout="horizontal" onFinish={handleSubmit}>
                <h3>{mfaSchoolRequirement ? 'Edit' : 'Configure'} MFA</h3>
                <Form.Item
                    label="MFA Level"
                    name="mfaLevel"
                    rules={[{ required: true, message: 'Please enter a school name' }]}
                    initialValue={mfaSchoolRequirement?.mfaLevel}
                >
                    <Select
                        options={[
                            { label: 'ALL', value: 'ALL' },
                            { label: 'ADMIN_ONLY', value: 'ADMIN_ONLY' }
                        ]}
                    ></Select>
                </Form.Item>
                <Form.Item
                    label="Admin Leeway"
                    name="adminLeeway"
                    rules={[{ required: true, message: 'Please enter a number' }]}
                    initialValue={mfaSchoolRequirement?.settings?.adminLeeway}
                >
                    <Input type="number" step="1" placeholder="in days" />
                </Form.Item>
                <Form.Item
                    label="User Leeway"
                    name="userLeeway"
                    rules={[{ required: true, message: 'Please enter a number' }]}
                    initialValue={mfaSchoolRequirement?.settings?.userLeeway}
                >
                    <Input type="number" step="1" placeholder="in days" />
                </Form.Item>
                <Flex>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={isLoading}>
                            Submit
                        </Button>
                    </Form.Item>
                </Flex>
            </Form>
        </Modal>
    )
}
